import md5 from 'md5'
import { getEnv } from '@unni/platform'

import { getAirbridgeTrackingLink } from '@/common/constants/airbridge'
import { isBrowser } from '@/utils/common'
import { amplitudeTracker } from '@/utils/amplitude'
import { Countries, countryToLocale, getCountryFromUrl } from '@/utils/language'
import { AmplitudeEventProps } from '@/utils/amplitude/types/event-name.type'
import { isPerformanceConsentGranted, isTargetingConsentGranted } from '@/features/cookie-consent/utils/consent-checker'
import { getUTMDataFromBrowser } from '@/shared/libs/utm-helper'

import { ParsedUrlQuery } from 'querystring'

declare global {
  interface Window {
    ga: UniversalAnalytics.ga
    dataLayer: unknown[]
    criteo_q: [...CriteoRequiredEvents, CriteoAdditionalEvents]
    MSStream?: unknown
    version: string
    COMMIT_HASH: string
  }
}

if (isBrowser()) {
  window.version = getEnv('NEXT_PUBLIC_VERSION') || ''
  window.COMMIT_HASH = getEnv('NEXT_PUBLIC_COMMIT_HASH') || ''
}

enum DeviceType {
  Desktop = 'd',
  Tablet = 't',
  Mobile = 'm',
}

type CriteoRequiredEvents = [CriteoSetAccountEvent, CriteoSetEmailEvent, CriteoSetSiteTypeEvent]

type CriteoAdditionalEvents =
  | CriteoViewHomeEvent
  | CriteoViewItemEvent
  | CriteoViewListEvent
  | CriteoTrackTransactionEvent

interface CriteoSetAccountEvent {
  event: 'setAccount'
  account: number
}

interface CriteoSetEmailEvent {
  event: 'setEmail'
  email: string
  hash_method: 'MD5'
}

interface CriteoSetSiteTypeEvent {
  event: 'setSiteType'
  type: DeviceType
}

interface CriteoViewHomeEvent {
  event: 'viewHome'
}

interface CriteoViewItemEvent {
  event: 'viewItem'
  item: string
}

interface CriteoViewListEvent {
  event: 'viewList'
  item: string[]
  category?: string
  keywords?: string
}

interface CriteoTrackTransactionEvent {
  event: 'trackTransaction'
  id: string
  item: CriteoTrackTransactionEventItem
}

export interface CriteoTrackTransactionEventItem {
  id: string
  price: number
  quantity: 1
}

export function isMobile() {
  if (typeof window === 'undefined' || !window.navigator) return false
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(window.navigator?.userAgent)
}

/**
 * _app.tsx에서 router 변경때마다 호출됩니다.
 * @param query 현재의 query 값을 의미합니다. location 변경때마다 router.query값으로 넘어옵니다.
 */
export function cacheQuerystring(query: ParsedUrlQuery): void {
  const savedQuery = JSON.parse(sessionStorage.getItem('url_qs') || '{}')
  const queryString = JSON.stringify(Object.assign(savedQuery, query))
  if (queryString && queryString !== '' && queryString !== '{}') {
    sessionStorage.setItem('url_qs', queryString)
  }
}

export function addGaPageView(pageName: string): void {
  if (!isBrowser()) return
  const country = getCountryFromUrl(location.pathname)
  const locale = countryToLocale(country)
  let maxAttempts = 5
  const interval = setInterval(() => {
    const win: Window = window
    try {
      const tracker = (win.ga?.getAll() || [])[0]
      if (tracker) {
        tracker.send('pageview', locale + pageName)
        clearInterval(interval) // 트래커가 준비되면 더 이상 반복하지 않음
      }
    } catch (e) {
      console.error(e)
    }

    maxAttempts--
    if (maxAttempts <= 0) {
      clearInterval(interval)
    }
  }, 1000)
}

export function addCriteoEvent(email: string, event: CriteoAdditionalEvents): void {
  if (!isTargetingConsentGranted()) return
  window.criteo_q = window.criteo_q || ([] as unknown as [...CriteoRequiredEvents, CriteoAdditionalEvents])

  const PARTNER_ID_OF_GANGNAM_UNNI = 75801

  const md5Email = md5(email)

  window.criteo_q.push(
    { event: 'setAccount', account: PARTNER_ID_OF_GANGNAM_UNNI },
    { event: 'setEmail', email: md5Email, hash_method: 'MD5' },
    { event: 'setSiteType', type: getDeviceTypeForCriteo(navigator.userAgent) },
    event,
  )
}

export function addDefaultTransactionEvent(email: string, consultationId: string): void {
  addCriteoEvent(email, {
    event: 'trackTransaction',
    id: consultationId,
    item: {
      id: `1`,
      price: 1,
      quantity: 1,
    },
  })
}

function getDeviceTypeForCriteo(userAgent: string): DeviceType {
  return /iPad/.test(userAgent)
    ? DeviceType.Tablet
    : /Mobile|iP(hone|od)|Android|BlackBerry|IEMobile|Silk/.test(userAgent)
      ? DeviceType.Mobile
      : DeviceType.Desktop
}

interface QueryParams {
  country?: Countries
  sourceDomain?: string
  [key: string]: string | number | undefined
}

/**
 * @description
 * Mobile인 경우 에어브리지 링크로 이동하는 링크를 생성합니다.
 * Desktop인 경우 국가별 Download QR코드 페이지로 이동하는 링크를 생성합니다.
 */
export function getDownloadLink(
  isOnlyTrackingLink = false,
  queryParams?: QueryParams,
  deeplinkParams?: Record<string, string>,
  options?: {
    disableConnectAmplitude?: boolean
  },
): string {
  /* 의존성 결정 */
  // 국가 정보가 없을 시 location.pathname에 따라 국가를 결정
  const country: Countries = getCountryFromUrl(location.pathname)

  // 캐싱된 queryParams 가져와서 합치기
  const urlPathname = location.pathname
  const cachedQuery = JSON.parse(sessionStorage.getItem('url_qs') || '{}')
  const query: Record<string, string> = { ...cachedQuery, ...(queryParams || {}) }

  let url = ''
  if (isMobile() || isOnlyTrackingLink) {
    // 모바일인 경우, 에어브리지 링크로 이동
    url = getAirbridgeDownloadLink(
      urlPathname,
      query,
      {
        ...(options?.disableConnectAmplitude ? {} : { ampDeviceId: amplitudeTracker.getDeviceId() || '' }),
        ...(deeplinkParams || {}),
      },
      country,
    )
  } else {
    // 데스크탑인 경우, 에어브리지 링크로 이동이 불가하므로 Qr Code /download 페이지로 유도함
    url = getQrDownloadPageLink(urlPathname, query, country)
  }

  return url
}

export const getQrDownloadPageLink = (
  urlPathname: string,
  query: Record<string, string>,
  country: Countries,
): string => {
  const refinedQuery = refineQuery(urlPathname, query, country)
  const link = `/${country}/download?${new URLSearchParams(refinedQuery).toString()}`
  return link
}

/**
 * 딥링크를 포함하는 airbridge 링크를 생성합니다.
 * @param urlPathname : url에 포함된 pathname
 * @param query  : 에어브리지에 추가할 파라미터
 * @param deeplinkParams : 딥링크에 추가할 파라미터
 * @param country
 * @returns
 */
const getAirbridgeDownloadLink = (
  urlPathname: string,
  query: Record<string, string>,
  deeplinkParams: Record<string, string>,
  country: Countries,
): string => {
  const refinedQuery = refineQuery(urlPathname, query, country)
  const link = getAirbridgeTrackingLink({
    key: country,
    channelName: refinedQuery['channel'],
  })
  const resultUrl = new URL(link)

  // URL 객체에 정제한 queryParams 추가
  Object.entries(refinedQuery).forEach(([key, value]) => {
    resultUrl.searchParams.append(key, value)
  })

  // deeplink_url queryParams에 추가
  resultUrl.searchParams.append('deeplink_url', getDeepLink(urlPathname, query, deeplinkParams || {}))
  return resultUrl.href
}

function refineQuery(urlPathname: string, query: Record<string, string>, country: Countries): Record<string, string> {
  const resultQuery: Record<string, string> = {}
  Object.keys(query).forEach((fieldName) => {
    switch (fieldName) {
      case 'utm_source':
      case 'utm_medium':
      case 'media_name':
      case 'pid':
        resultQuery['channel'] = query[fieldName]
        break
      case 'sourceDomain':
      case 'utm_campaign':
      case 'c':
        resultQuery['campaign'] = query[fieldName]
        break
      case 'utm_content':
        resultQuery['ad_group'] = query[fieldName]
        break
      case 'utm_term':
        resultQuery['ad_creative'] = query[fieldName]
        break
      case 'term':
        resultQuery['term'] = query[fieldName]
        break
      default:
        resultQuery[fieldName] = query[fieldName]
    }
  })

  // 쿠키에 저장된 utm_data가 있는 경우 오버라이드
  const utmData = getUTMDataFromBrowser()
  if (utmData) {
    if (utmData.utm_source) {
      resultQuery['channel'] = utmData.utm_source
    }
    if (utmData.utm_campaign) {
      resultQuery['campaign'] = utmData.utm_campaign
    }
    if (utmData.utm_content) {
      resultQuery['ad_group'] = utmData.utm_content
    }
    if (utmData.utm_term) {
      resultQuery['ad_creative'] = utmData.utm_term
    }
    if (utmData.term) {
      resultQuery['term'] = utmData.term
    }
  }

  // channel, campaign이 없는 경우, 기본값 설정
  if (!resultQuery['channel']) {
    resultQuery['channel'] = `cheetah_${country}`
  }
  if (!resultQuery['campaign']) {
    resultQuery['campaign'] = getSourceDomainFromUrlPath(urlPathname)
  }

  return resultQuery
}

function getDeepLink(
  urlPathname: string,
  query: Record<string, string>,
  deeplinkParams?: Record<string, string>,
): string {
  /* path 결정 */
  let deepLink = 'hbeauty://'
  if (/hospitals\/([0-9]+)/.test(urlPathname)) {
    const match = urlPathname.match(/hospitals\/([0-9]+)/)
    const hospitalId = match !== null ? match[1] : null
    deepLink += `hospital/${hospitalId}`
  } else if (urlPathname.includes('reviews') && query.hospitalId) {
    const hospitalId = query.hospitalId
    deepLink += `hospital/${hospitalId}`
  } else if (urlPathname.includes('reviews') && query.eventId) {
    const eventId = query.eventId
    deepLink += `event/${eventId}`
  } else if (/events\/([0-9]+)/.test(urlPathname)) {
    const match = urlPathname.match(/events\/([0-9]+)/)
    const eventId = match !== null ? match[1] : null
    deepLink += `event/${eventId}`
  } else if (/doctors\/([0-9]+)/.test(urlPathname)) {
    const match = urlPathname.match(/doctors\/([0-9]+)/)
    const doctorId = match !== null ? match[1] : null
    deepLink += `doctor/${doctorId}`
  } else if (/community\/([0-9]+)/.test(urlPathname)) {
    const match = urlPathname.match(/community\/([0-9]+)/)
    const communityDocumentId = match !== null ? match[1] : null
    deepLink += `document/${communityDocumentId}`
  } else if (urlPathname.endsWith('search')) {
    deepLink += 'search'
  } else if (urlPathname.endsWith('events')) {
    deepLink += 'search' // 앱 검색은 탭으로 이동 가능하므로 search로 처리 합니다.
  }

  const deeplinkUrl = new URL(deepLink)

  /* query 추가 */
  if (deepLink.includes('search') && query.q) {
    deeplinkUrl.searchParams.append('q', query.q)
  }

  for (const [key, value] of Object.entries(deeplinkParams || {})) {
    deeplinkUrl.searchParams.append(key, value)
  }

  return deeplinkUrl.href
}

enum SourceDomain {
  Home = 'home',
  Column = 'column',
  Hospital = 'hospital',
  Event = 'event',
  Community = 'community',
  CheetahJP = 'cheetah_jp',
  CheetahKR = 'cheetah_kr',
  CheetahUS = 'cheetah_us',
  CheetahTH = 'cheetah_th',
  CheetahCN = 'cheetah_cn',
  CheetahTW = 'cheetah_tw',
  Search = 'search',
  Review = 'review',
  About = 'about',
}

export function getSourceDomainFromUrlPath(pathname: string): SourceDomain {
  if (['/', '/kr', '/jp', '/cn', '/tw'].includes(pathname)) {
    return SourceDomain.Home
  }
  if (/posts/.test(pathname)) {
    return SourceDomain.Column
  }
  if (/hospital/.test(pathname)) {
    return SourceDomain.Hospital
  }
  if (/event/.test(pathname)) {
    return SourceDomain.Event
  }
  if (/community/.test(pathname)) {
    return SourceDomain.Community
  }
  if (/search/.test(pathname)) {
    return SourceDomain.Search
  }
  if (/reviews/.test(pathname)) {
    return SourceDomain.Review
  }
  if (/about/.test(pathname)) {
    return SourceDomain.About
  }
  if (pathname.includes('/jp')) {
    return SourceDomain.CheetahJP
  }
  if (pathname.includes('/us')) {
    return SourceDomain.CheetahUS
  }
  if (pathname.includes('/th')) {
    return SourceDomain.CheetahTH
  }
  if (pathname.includes('/cn')) {
    return SourceDomain.CheetahCN
  }
  if (pathname.includes('/tw')) {
    return SourceDomain.CheetahTW
  }
  return SourceDomain.CheetahKR
}

export function postGA(category: string, action: string, label?: string | number): void {
  if (!isBrowser()) return
  retry(
    (attempts) => {
      const win: Window = window
      const tracker = (win.ga?.getAll?.() || [])[0]
      if (tracker) {
        tracker.send('event', category, action, label)
      } else {
        throw new Error(`postGA: ${attempts}번째 시도 중 Error 발생`)
      }
    },
    5,
    1000,
  )
}

export function postAmplitude<K extends keyof AmplitudeEventProps>(
  name: K | (string & NonNullable<unknown>), // TODO. 추후에는 string을 제거하고 K로 변경해야함
  properties?: AmplitudeEventProps[K],
): void {
  if (!isPerformanceConsentGranted() && !window.location.pathname.includes('unni-guide')) return
  try {
    amplitudeTracker.logEvent(`web.${name}`, properties || {})
  } catch {
    console.log('postAmplitude | error on amplitudeTracker.logEvent')
  }
}

export function translateOpTag(opTag: string, targetLang: string): string {
  const trsMap = new Map()

  if (/ja/.test(targetLang)) {
    trsMap.set('가슴', 'バスト')
    trsMap.set('눈', '目')
    trsMap.set('쁘띠', 'プチ')
    trsMap.set('모발이식', '')
    trsMap.set('지방흡입', '脂肪吸引')
    trsMap.set('반영구화장', '')
    trsMap.set('보톡스', 'ボトックス')
    trsMap.set('안면윤곽/양악', '顔面輪郭/両顎')
    trsMap.set('기타', 'その他')
    trsMap.set('여성성형', '')
    trsMap.set('입', '口')
    trsMap.set('치아/시력교정', '歯/視力矯正')
    trsMap.set('제모', '')
    trsMap.set('지방이식', '脂肪移植')
    trsMap.set('코', '鼻')
    trsMap.set('피부', '皮膚')
    trsMap.set('필러', 'フィラー')
    trsMap.set('양악', '')
    trsMap.set('한방', '')
    trsMap.set('남성성형', '')

    opTag = trsMap.get(opTag)
  }

  return opTag
}

export function getMobileOS(): 'server' | 'Windows Phone' | 'Android' | 'iOS' | 'unknown' {
  if (!process.browser) {
    return 'server'
  }

  const userAgent = navigator.userAgent || navigator.vendor

  // Windows Phone must come first because its UA also contains "Android"
  if (/windows phone/i.test(userAgent)) {
    return 'Windows Phone'
  }

  if (/android/i.test(userAgent)) {
    return 'Android'
  }

  // iOS detection from: http://stackoverflow.com/a/9039885/177710
  if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
    return 'iOS'
  }

  return 'unknown'
}

function _numberFormat(x: number) {
  return x.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')
}

export function numberToText(number: number, target = 'ko'): string {
  const unitWordsList: {
    ko: string[]
    ja: string[]
  } = {
    ko: ['', '만', '억', '조', '경'],
    ja: ['', '万', '億', '兆', '京'],
  }
  const unitWords = unitWordsList[target === 'ja' ? 'ja' : 'ko']
  const inputNumber = number
  const splitUnit = 10000
  const splitCount = unitWords.length
  const resultArray = []
  let resultString = ''

  for (let i = 0; i < splitCount; i++) {
    let unitResult = (inputNumber % Math.pow(splitUnit, i + 1)) / Math.pow(splitUnit, i)
    unitResult = Math.floor(unitResult)
    if (unitResult > 0) {
      resultArray[i] = unitResult
    }
  }

  for (let i = 0; i < resultArray.length; i++) {
    if (!resultArray[i]) {
      continue
    }
    resultString = String(_numberFormat(resultArray[i])) + unitWords[i] + resultString
  }

  return resultString
}

export function postGtag(eventName: string, properties: object = {}): void {
  if (!isPerformanceConsentGranted()) return
  if (!isBrowser()) return
  retry(
    () => {
      window.dataLayer.push({ event: eventName, ...properties })
    },
    5,
    1000,
  )
}

export const setGA4UserProperties = (userId: number, properties = {}) => {
  if (!isBrowser()) return
  retry(
    () => {
      window.gtag('set', 'user_data', {
        user_id: userId,
        ...properties,
      })
    },
    5,
    1000,
  )
}

/**
 * callback 함수를 재시도하는 함수입니다.
 * Exponential Backoff 알고리즘을 사용합니다.
 * @param callback
 * callback에서 error를 throw하는 경우, retry하며,
 * 마지막 retry에서도 error가 발생하면 console.error로 출력합니다.
 * @param retries 재시도 횟수 (기본값은 3)
 * @param delay 재시도 간격 (기본값은 1초)
 */
export function retry(
  callback: (attempts: number) => void,
  retries: number = 3,
  delay: number = 1000,
  maxDelay = 16000,
): void {
  let attempts = 0
  const tryCallback = () => {
    attempts++
    try {
      callback(attempts)
    } catch (err) {
      if (attempts < retries) {
        const nextDelay = Math.min(delay * Math.pow(2, attempts - 1), maxDelay)
        setTimeout(tryCallback, nextDelay)
      } else {
        /**
         * 구현 의도
         * 매시도에서 error 메시지를 발생시키지 않고, 최종적으로 실행되지 않은 경우에만 console.error로 출력합니다.
         * 추후 error 로그가 수집될 (datadog 등) 때 과수집을 방지하기 위함입니다.
         * _modn
         */
        console.error(err)
      }
    }
  }
  tryCallback()
}
